/**
 * Maps elder clinical profile + risk assessment → marketplace caregiver match criteria.
 * Aligned with NEMA match-making spec (skill set, patient types, shift, location).
 */

import { resolveIndianCity } from "./indianCities";
import {
  DEFAULT_MARKETPLACE_RADIUS_KM,
  CITY_MARKETPLACE_RADIUS_KM,
  searchShiftToShiftType,
} from "./marketplaceFilterEngine";
import { filtersToMarketplaceApiParams } from "../utils/marketplaceApiFilters";

const DIAGNOSIS_MATCH = {
  diabetes: {
    patientConditions: ["Elderly Care"],
    skills: ["Sugar Monitoring", "BP Monitoring"],
    careType: "attendant",
  },
  hypertension: {
    patientConditions: ["Elderly Care"],
    skills: ["BP Monitoring"],
    careType: "attendant",
  },
  stroke: {
    patientConditions: ["Stroke", "Paralysis"],
    skills: ["Mobility Support", "Wheelchair Transfer"],
    careType: "attendant",
  },
  dementia: {
    patientConditions: ["Dementia", "Alzheimer's"],
    skills: ["Dementia Support"],
    careType: "dementia",
  },
  parkinsons: {
    patientConditions: ["Parkinson's"],
    skills: ["Mobility Support"],
    careType: "attendant",
  },
  heart_disease: {
    patientConditions: ["Elderly Care"],
    skills: ["BP Monitoring", "Oxygen Support"],
    careType: "attendant",
  },
  cancer: {
    patientConditions: ["Cancer Care", "Palliative Care"],
    skills: ["Feeding Assistance"],
    careType: "nurses",
  },
  arthritis: {
    patientConditions: ["Orthopedic", "Elderly Care"],
    skills: ["Mobility Support", "Exercise Assistance"],
    careType: "attendant",
  },
  post_surgery: {
    patientConditions: ["Post Surgery"],
    skills: ["Mobility Support", "Wound Dressing"],
    careType: "post_operative",
  },
  other: {
    patientConditions: ["Elderly Care"],
    skills: [],
    careType: "attendant",
  },
};

const RISK_ANSWER_MATCH = {
  fallHistory: {
    one_fall: { skills: ["Mobility Support"] },
    multiple_falls: {
      patientConditions: ["Bedridden"],
      skills: ["Mobility Support", "Wheelchair Transfer"],
    },
  },
  memoryStatus: {
    mild_forgetfulness: { patientConditions: ["Elderly Care"] },
    mild_aggressive_dementia: {
      patientConditions: ["Dementia", "Alzheimer's"],
      skills: ["Dementia Support"],
      careType: "dementia",
    },
    dementia_alz_wandering: {
      patientConditions: ["Dementia", "Alzheimer's"],
      skills: ["Dementia Support"],
      careType: "nurses",
    },
  },
  mobilityTransfer: {
    walking_support: { skills: ["Mobility Support", "Wheelchair Transfer"] },
    wheelchair_bedridden_transfer: {
      patientConditions: ["Bedridden"],
      skills: ["Wheelchair Transfer", "Diaper Management", "Feeding Assistance"],
    },
  },
  medicalCondition: {
    stable_chronic: { skills: ["BP Monitoring", "Sugar Monitoring"] },
    recovering_surgery_stroke: {
      patientConditions: ["Post Surgery", "Stroke"],
      skills: ["Mobility Support", "Wound Dressing"],
      careType: "post_operative",
    },
    advanced_neuro_palliative: {
      patientConditions: ["Palliative Care", "Bedridden"],
      skills: ["Feeding Assistance", "Oxygen Support", "Catheter Care"],
      careType: "nurses",
    },
  },
  dailyCareRequirement: {
    basic_companionship: { patientConditions: ["Elderly Care"] },
    bathing_toileting_feeding: {
      skills: ["Feeding Assistance", "Diaper Management", "Personal Care"],
    },
    complete_assistance: {
      patientConditions: ["Bedridden"],
      skills: ["Feeding Assistance", "Diaper Management", "Mobility Support"],
      careType: "nurses",
    },
  },
};

const FLAG_MATCH = {
  fall_risk: { skills: ["Mobility Support"] },
  dementia: {
    patientConditions: ["Dementia", "Alzheimer's"],
    skills: ["Dementia Support"],
    careType: "dementia",
  },
  bedridden: {
    patientConditions: ["Bedridden"],
    skills: ["Diaper Management", "Feeding Assistance"],
  },
  mobility: { skills: ["Mobility Support", "Wheelchair Transfer"] },
  daily_tasks: { skills: ["Feeding Assistance", "Personal Care"] },
  chronic: { skills: ["BP Monitoring", "Sugar Monitoring"] },
};

const RISK_LEVEL_MATCH = {
  low: { careType: "attendant", minExperience: 1, verifiedOnly: false },
  moderate: { careType: "attendant", minExperience: 3, verifiedOnly: false },
  high: { careType: "nurses", minExperience: 5, verifiedOnly: true },
};

function uniqueList(items = []) {
  return [...new Set(items.map((s) => String(s).trim()).filter(Boolean))];
}

function mergeMatch(target, source = {}) {
  if (!source) return;
  if (source.patientConditions?.length) {
    target.patientConditions.push(...source.patientConditions);
  }
  if (source.skills?.length) {
    target.skills.push(...source.skills);
  }
  if (source.careType) {
    target.careType = source.careType;
  }
  if (source.nurseClinical?.length) {
    target.nurseClinical.push(...source.nurseClinical);
  }
}

function careTypePriority(type) {
  const order = { attendant: 1, dementia: 2, post_operative: 3, physio: 4, nurses: 5 };
  return order[type] || 1;
}

function pickCareType(current, next) {
  if (!next) return current;
  if (!current) return next;
  return careTypePriority(next) > careTypePriority(current) ? next : current;
}

/**
 * @returns {{
 *   careType: string,
 *   patientConditions: string[],
 *   skills: string[],
 *   shiftType?: string,
 *   minExperience: number,
 *   verifiedOnly: boolean,
 *   nurseClinical: string[],
 *   radiusKm: number,
 *   matchReasons: string[],
 * }}
 */
export function deriveElderMatchCriteria({ senior = {}, assessment = {}, searchContext = {} } = {}) {
  const criteria = {
    careType: searchContext.careType || "attendant",
    patientConditions: ["Elderly Care"],
    skills: [],
    shiftType: searchContext.searchShift
      ? searchShiftToShiftType(searchContext.searchShift)
      : searchContext.shiftType || undefined,
    minExperience: 1,
    verifiedOnly: false,
    nurseClinical: [],
    radiusKm: Number(searchContext.radiusKm) || DEFAULT_MARKETPLACE_RADIUS_KM,
    matchReasons: [],
  };

  const diagnosisKey = senior.diagnosisKey || "";
  if (DIAGNOSIS_MATCH[diagnosisKey]) {
    mergeMatch(criteria, DIAGNOSIS_MATCH[diagnosisKey]);
    criteria.matchReasons.push(`Primary diagnosis: ${diagnosisKey}`);
  }

  const riskAnswers = assessment.riskAnswers || {};
  Object.entries(RISK_ANSWER_MATCH).forEach(([questionId, options]) => {
    const answer = riskAnswers[questionId];
    if (answer && options[answer]) {
      mergeMatch(criteria, options[answer]);
      criteria.matchReasons.push(`${questionId}: ${answer}`);
    }
  });

  (assessment.flags || []).forEach((flag) => {
    if (FLAG_MATCH[flag]) {
      mergeMatch(criteria, FLAG_MATCH[flag]);
      criteria.matchReasons.push(`Flag: ${flag}`);
    }
  });

  const riskLevel = assessment.riskLevel || "low";
  const levelMeta = RISK_LEVEL_MATCH[riskLevel] || RISK_LEVEL_MATCH.low;
  criteria.careType = pickCareType(criteria.careType, levelMeta.careType);
  criteria.minExperience = Math.max(criteria.minExperience, levelMeta.minExperience);
  criteria.verifiedOnly = criteria.verifiedOnly || levelMeta.verifiedOnly;

  if (riskLevel === "high") {
    criteria.nurseClinical.push("Bedside Monitoring");
    if (criteria.patientConditions.some((c) => /palliative|bedridden/i.test(c))) {
      criteria.nurseClinical.push("Wound Dressing", "Catheter Care");
    }
  }

  criteria.patientConditions = uniqueList(criteria.patientConditions);
  criteria.skills = uniqueList(criteria.skills);
  criteria.nurseClinical = uniqueList(criteria.nurseClinical);

  return criteria;
}

export function resolveElderSearchCoords(senior = {}, searchContext = {}) {
  if (searchContext.latitude != null && searchContext.longitude != null) {
    return {
      latitude: Number(searchContext.latitude),
      longitude: Number(searchContext.longitude),
    };
  }
  const label = searchContext.searchLocation || senior.address || "";
  const city = resolveIndianCity(label);
  if (!city) return null;
  return { latitude: city.latitude, longitude: city.longitude };
}

export function buildMarketplaceQueryFromElder({ senior, assessment, searchContext } = {}) {
  const criteria = deriveElderMatchCriteria({ senior, assessment, searchContext });
  const coords = resolveElderSearchCoords(senior, searchContext);
  const filterParams = filtersToMarketplaceApiParams(
    {
      careType: criteria.careType,
      patientConditions: criteria.patientConditions,
      skills: criteria.skills,
      shiftType: criteria.shiftType,
      minExperience: criteria.minExperience,
      verifiedOnly: criteria.verifiedOnly,
      nurseClinical: criteria.nurseClinical,
    },
    { serviceTab: criteria.careType, radiusKm: criteria.radiusKm }
  );
  if (searchContext?.searchDate) {
    filterParams.serviceDate = String(searchContext.searchDate).slice(0, 10);
  }

  return { criteria, coords, filterParams };
}

export function buildRelaxedMarketplaceQueryFromElder({ senior, assessment, searchContext } = {}) {
  const { criteria, coords } = buildMarketplaceQueryFromElder({ senior, assessment, searchContext });
  const filterParams = filtersToMarketplaceApiParams(
    {
      careType: criteria.careType,
      patientConditions: criteria.patientConditions.slice(0, 2),
      shiftType: criteria.shiftType,
      minExperience: 0,
      verifiedOnly: false,
    },
    { serviceTab: criteria.careType, radiusKm: Math.max(criteria.radiusKm || 0, CITY_MARKETPLACE_RADIUS_KM) }
  );
  if (searchContext?.searchDate) {
    filterParams.serviceDate = String(searchContext.searchDate).slice(0, 10);
  }
  return { criteria, coords, filterParams };
}

function norm(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function overlapScore(needles = [], haystack = []) {
  if (!needles.length || !haystack.length) return 0;
  const lowerHay = haystack.map(norm);
  return needles.reduce((sum, needle) => {
    const n = norm(needle);
    return sum + (lowerHay.some((h) => h.includes(n) || n.includes(h)) ? 1 : 0);
  }, 0);
}

/** Rank caregivers by how well they match derived elder criteria. */
export function scoreCaregiverMatch(caregiver = {}, criteria = {}) {
  const skills = Array.isArray(caregiver.skills)
    ? caregiver.skills
    : String(caregiver.skills || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

  let score = 0;
  score += overlapScore(criteria.patientConditions, skills) * 4;
  score += overlapScore(criteria.skills, skills) * 3;

  const role = norm(caregiver.serviceRole || caregiver.careType);
  const wanted = norm(criteria.careType);
  if (wanted && role === wanted) score += 5;
  if (wanted === "dementia" && skills.some((s) => /dementia|alzheimer/i.test(s))) score += 4;
  if (wanted === "nurses" && role === "nurses") score += 4;

  const years = Number(caregiver.years ?? caregiver.experience_years);
  if (Number.isFinite(years) && years >= (criteria.minExperience || 0)) score += 2;

  if (caregiver.rating != null) score += Number(caregiver.rating) / 2;
  if (caregiver.isAvailable !== false) score += 1;

  return score;
}

export function rankCaregiversForElder(caregivers = [], criteria = {}) {
  return [...caregivers]
    .map((caregiver) => ({
      caregiver,
      score: scoreCaregiverMatch(caregiver, criteria),
    }))
    .sort((a, b) => b.score - a.score)
    .map((row) => row.caregiver);
}
